import {Grid} from '@mui/material'
import {Box, Typography} from '@viptech/react-components'
import {useEffect, useState} from 'react'
import {AspectRatio} from 'react-aspect-ratio'
import {useNavigate} from 'react-router-dom'
import {CountingFacesDTO} from 'src/hooks/api/personalized-dashboard/useGetTotalFromCountingFaces'
import useWebsocket from 'src/hooks/api/socket/useWebsocket'
import VideoPlayer from 'src/pages/camera-config/components/camera-general-data/VideoPlayer'
import {LayoutTypes} from 'src/pages/camera-mosaic'
import FacesCountingEventList from 'src/pages/camera-mosaic/components/FacesCountingEventList'
import PlayerBar from 'src/pages/camera-mosaic/components/PlayerBar'

import api, {Camera} from 'src/services/api'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'

export type defaultProps = {
  cameras: Camera[]
  layoutType: number
  showBarriers: boolean
}

export type StreamsAsync = {tag: 'Empty'} | {tag: 'Loading'} | {tag: 'Success'; data: Camera; hash: string} | {tag: 'Error'; data: Camera}

const sizes = [1, 2, 4, 9, 16] as const

function LayoutDefault(props: defaultProps) {
  const {cameras, layoutType, showBarriers} = props

  function getMosaicSize(cameraCount: number) {
    return sizes.find((size) => size >= Math.max(cameraCount, 1)) ?? sizes[sizes.length - 1]
  }
  const [streams, setStreams] = useState<StreamsAsync[]>([{tag: 'Empty'}])

  const navigate = useNavigate()

  const getColumnQuantity = () => {
    switch (getMosaicSize(cameras.length)) {
      case 1:
      case 2:
        return {sm: 12, xl: 12}
      case 4:
        return {xs: 12, md: 6, sm: 6, lg: 6, xl: 6}
      case 9:
        return {xs: 12, sm: 6, md: 4, lg: 4, xl: 4}
      case 16:
        return {xs: 12, sm: 6, md: 3, xl: 3}
      default:
        break
    }
  }

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        }
      } else {
        navigate(-1)
      }
    }
  }

  function handleFullscreenChange() {
    if (!document.fullscreenElement) {
      navigate(-1)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress)
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const [offsetStream, setOffsetStream] = useState<number>()

  useEffect(() => {
    async function loadConnection() {
      const streams: StreamsAsync[] = []
      cameras.forEach((camera, index) => {
        const stream = api.streaming.startStreamWithCameraId(camera.id, offsetStream)
        streams[index] = {tag: 'Success', data: camera, hash: stream}
      })
      if (!cameras.length) streams.push({tag: 'Empty'})
      setStreams(streams)
    }
    loadConnection()
  }, [cameras, offsetStream])

  const [socketCounting, setSocketCounting] = useState<CountingFacesDTO>()

  const onMessage = (counting: CountingFacesDTO) => {
    setSocketCounting(counting)
  }

  useWebsocket({
    room: WebSocketRoomTypeEnum.COUNTING,
    listenTo: [FaceDetectionWSChannelEnum.COUTING],
    onMessage,
    enable: layoutType === LayoutTypes.Facial,
  })

  const [playbackSpeeedRate, setPlaybackSpeeedRate] = useState<number>(1)
  const [isErrorOnStream, setIsErrorOnStream] = useState(false)

  return (
    <Box display='flex' direction='column' height='100%' justifyContent='space-between'>
      <Grid
        container
        sx={{
          flexFlow: streams.length > 2 ? 'wrap' : 'row',
          height: streams.length < 2 ? '100%' : undefined,
        }}>
        {streams.map((streamAsync, index) => (
          <Grid item {...getColumnQuantity()} sx={{display: 'flex'}} key={streamAsync.tag + index}>
            <Box display='flex' align='center' justifyContent='center' width='100%' backgroundColor='#000000' border='2px solid #3E3E3E'>
              {streamAsync.tag === 'Empty' && (
                <Box width='100%' display='flex' direction='column' align='center' justifyContent='center'>
                  <Typography variant='p' children='Nenhuma câmera' />
                </Box>
              )}

              {streamAsync.tag === 'Success' && (
                <Box width='100%' position='relative' display='flex' direction='column'>
                  <Box padding='5px'>
                    <Typography variant='p' color='white' children={`#(${streamAsync.data.id}) ${streamAsync.data.name}`} />
                  </Box>

                  <AspectRatio ratio={16 / 9}>
                    <VideoPlayer
                      src={streamAsync.hash}
                      height='100%'
                      cameraId={streamAsync.data.id}
                      drawBarrierCoordinates={showBarriers}
                      playbackSpeeedRate={playbackSpeeedRate}
                      setIsErrorOnStream={setIsErrorOnStream}
                    />
                  </AspectRatio>
                  {isErrorOnStream && (
                    <Box width='100%' display='flex' direction='column' align='center' justifyContent='center' height='250px'>
                      <Typography variant='p'>Não foi possível reproduzir o vídeo</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {streamAsync.tag === 'Success' && (
              <Box height='100%'>
                {layoutType === LayoutTypes.Facial && (
                  <FacesCountingEventList
                    mosaico={getMosaicSize(cameras.length)}
                    camera={streamAsync.data.id}
                    companyId={streamAsync.data.companyId}
                    socketCounting={socketCounting}
                  />
                )}
              </Box>
            )}
          </Grid>
        ))}
      </Grid>
      {cameras.length > 0 && (
        <PlayerBar
          setOffset={setOffsetStream}
          offset={offsetStream}
          setPlaybackSpeeedRate={setPlaybackSpeeedRate}
          playbackSpeeedRate={playbackSpeeedRate}
          cameraIds={cameras.map((it) => it.id)}
        />
      )}
    </Box>
  )
}

export default LayoutDefault
